import { Link } from '@tanstack/react-router'
import { AgentGlyph, ContextRing } from '@viko/transcript-ui'
import { CircleAlert, GitMerge, Hammer, Package } from 'lucide-react'
import type { LandingChip } from '../lib/landing-status.js'
import type { Machine, RunSummary } from '../lib/types.js'
import {
  type AccountRef,
  chargeLabel,
  selectedAccount,
  selectRowUsage,
  type TokenRates,
} from '../lib/usage-accounting.js'
import { Ago } from './ui.js'

export function statusOf(r: RunSummary): { label: string; tone: string; live: boolean } {
  if (r.typingElsewhere) return { label: 'In a terminal', tone: 'text-status-run', live: true }
  if (r.readOnly) return { label: 'Imported', tone: 'text-status-idle', live: false }
  if (r.status === 'waiting') return { label: 'Needs you', tone: 'text-status-wait', live: true }
  if (r.status === 'running' || r.status === 'compacting')
    return {
      label: r.status === 'compacting' ? 'Compacting' : 'Working',
      tone: 'text-status-run',
      live: true,
    }
  if (r.status === 'error') return { label: 'Failed', tone: 'text-status-fail', live: false }
  if (r.landing?.state === 'landed') return { label: 'Landed', tone: 'text-status-ok', live: false }
  if (r.landing?.state === 'ready')
    return { label: 'Ready to land', tone: 'text-accent-text', live: false }
  // Same vocabulary as the session header: an open session is idle, never "Done".
  if (r.status === 'idle') return { label: 'Idle', tone: 'text-status-idle', live: false }
  return { label: 'Finished', tone: 'text-status-idle', live: false }
}

export function RunRow({
  run,
  machines,
  to,
  current,
  account: accountProp,
  landingChip,
  view,
  rates,
}: {
  run: RunSummary
  machines?: Machine[]
  to: string
  current?: boolean
  /** Async landing status (queued / verifying / landed / blocked), see lib/landing-status. */
  landingChip?: LandingChip
  /** Provider account this run uses (defaults to `selectedAccount(run, machines)`). */
  account?: AccountRef
  /** Decrypted transcript view when open: preferred over the row's report fallback. */
  view?: Parameters<typeof selectRowUsage>[1]
  rates?: TokenRates
}) {
  const s = statusOf(run)
  const usage = selectRowUsage(run, view, rates ? { rates } : {})
  const account = accountProp ?? selectedAccount(run, machines)
  const machine = machines?.find((m) => m.id === run.machineId)?.name
  return (
    <Link to={to} className="row" aria-current={current ? 'page' : undefined} data-run={run.runId}>
      <span className="relative mt-0.5 grid h-8 w-8 flex-none place-items-center rounded-full bg-surface-2">
        <AgentGlyph agent={run.agent ?? 'claude'} size={15} />
        <span
          className={`absolute -right-0.5 -bottom-0.5 rounded-full border-2 border-bg ${s.tone}`}
        >
          <span
            className={`pulse ${s.live && run.status !== 'waiting' ? 'is-live' : ''}`}
            aria-hidden="true"
          />
        </span>
      </span>
      <span className="grid min-w-0 flex-1 gap-0.5">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="row__title truncate">{run.title ?? run.runId}</span>
        </span>
        <span className="row__meta flex min-w-0 items-center gap-1.5">
          <span className={`font-semibold ${s.tone}`}>{s.label}</span>
          <span aria-hidden="true">·</span>
          <span className="truncate">
            {run.projectName ?? 'Project'}
            {machine ? ` on ${machine}` : ''}
          </span>
          <span aria-hidden="true">·</span>
          <span className="flex-none">
            <Ago ts={run.updatedAt} />
          </span>
        </span>
        <span className="row__meta flex min-w-0 flex-wrap items-center gap-x-1.5" data-usage-line>
          {account ? (
            <>
              <span className="truncate">{account.label}</span>
              <span aria-hidden="true">·</span>
            </>
          ) : null}
          {run.workstreamName ? (
            <>
              <span className="truncate">{run.workstreamName}</span>
              <span aria-hidden="true">·</span>
            </>
          ) : null}
          <span data-charge={usage.charge.kind}>
            {chargeLabel(usage.charge)}
            {usage.observedAt && usage.charge.kind !== 'missing' ? (
              <>
                {' · '}
                <Ago ts={usage.observedAt} />
              </>
            ) : null}
          </span>
        </span>
        {run.status === 'waiting' && run.blockedOn ? (
          <span className="mt-1 flex items-start gap-1.5 text-[14px] leading-[20px] text-text-1">
            <CircleAlert
              size={14}
              className="mt-0.5 flex-none text-status-wait"
              aria-hidden="true"
            />
            <span className="line-clamp-2">{run.blockedOn}</span>
          </span>
        ) : run.activity && s.live ? (
          <span className="mt-0.5 flex items-center gap-1.5 text-[14px] leading-[20px] text-text-2">
            <Hammer size={13} className="flex-none text-text-3" aria-hidden="true" />
            <span className="truncate">{run.activity}</span>
          </span>
        ) : null}
        {run.latestBuild || landingChip || run.landing?.state === 'ready' ? (
          <span className="mt-1 flex flex-wrap gap-1.5">
            {run.latestBuild ? (
              <span className="vk-chip">
                <Package size={12} aria-hidden="true" />
                <span className="vk-chip__text">
                  Build {run.latestBuild.number} · {run.latestBuild.lane} ·{' '}
                  {run.latestBuild.state === 'ok'
                    ? 'available'
                    : run.latestBuild.state === 'error'
                      ? 'failed'
                      : run.latestBuild.state === 'pending'
                        ? 'queued'
                        : 'building'}
                </span>
              </span>
            ) : null}
            {landingChip ? (
              <span
                className={`vk-chip vk-tone-${landingChip.tone}`}
                title={landingChip.title ?? landingChip.label}
                data-landing-chip
              >
                <GitMerge size={12} aria-hidden="true" />
                <span className="vk-chip__text">{landingChip.label}</span>
              </span>
            ) : run.landing?.state === 'ready' ? (
              <span className="vk-chip vk-tone-accent">
                <GitMerge size={12} aria-hidden="true" />
                <span className="vk-chip__text">{run.branch} ready to land</span>
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
      {run.contextPct != null && s.live ? (
        <span className="flex-none pt-1">
          <ContextRing pct={run.contextPct} size={20} />
        </span>
      ) : null}
    </Link>
  )
}
